import {makeAutoObservable, observable, runInAction} from 'mobx';
import type {
  StudioRequestOptions,
  StudioVersion,
} from '../../../data/api/StudioApi';
import StudioRepository from '../../../data/repositories/StudioRepository';
import type {JobView} from '../types';
import {isQueued} from '../queue';
import {cancelJob, isExporting, isWorking, waitingStage} from './jobStatus';
import {scopedIdentity} from './MutationScope';
import {backoff, pollAfter, POLL_MAX_MS, POLL_WAIT_MS} from './polling';
import type {Backoff} from './polling';
import {studioRequest} from './studioRequests';

/** A new view, or a function of the latest one (as React's setState takes). */
export type JobViewUpdate =
  | JobView
  | ((current: JobView | null) => JobView | null);

/** How often the open job is read again: quickly while Studio works on it. */
export const POLL_BUSY_MS = 900;
export const POLL_IDLE_MS = 4000;
export {POLL_MAX_MS, POLL_WAIT_MS};

/**
 * When to read the job again after a read that showed `view` (null when there was nothing
 * new to show) or failed. While the job waits in a queue or its MP4s export (its previews
 * already play), the polls slow from 2 s to 10 s for as long as the stage stays the same; a
 * failed read waits 4 s, then twice as long each time, up to 10 s.
 */
export function nextPoll(
  view: JobView | null,
  failed: boolean,
  previous: Backoff | null,
): Backoff {
  if (failed) return backoff(previous, 'failed', POLL_IDLE_MS, 2);
  if (view && isWorking(view)) {
    const {job} = view;
    if (waitingStage(job) || isExporting(job) || isQueued(job))
      return backoff(
        previous,
        `${job.status}:${job.stage}:${job.queue?.ahead ?? ''}`,
        POLL_WAIT_MS,
      );
    return {key: 'busy', delay: POLL_BUSY_MS};
  }
  return {key: 'idle', delay: POLL_IDLE_MS};
}

/** What a page sends a change against, from the job it has open. */
export interface OpenJob {
  organizationId: string | undefined;
  id: string;
  storeVersion: StudioVersion;
}

/**
 * The Studio job a page has open: it reads the job, keeps reading it while Studio works on it,
 * and shows the job each change returns. A newer read or change always wins over an older read
 * that finishes later, and nothing from one job ever shows on another.
 *
 * A page opens a job with `open(organizationId, id)` (in an effect, returning what it returns)
 * and reads `view` and `error`. Registered in storesProvider as `studioJobStore`.
 */
export default class StudioJobStore {
  /** The open job's organization and id. */
  organizationId: string | undefined = undefined;
  jobId: string | undefined = undefined;
  /** The last read failure; cleared by the next read or change that succeeds. */
  error: Error | null = null;

  private shown: JobView | null = null;
  private shownFor: string | null = null;
  private repository = new StudioRepository();
  private lifecycle: AbortController | null = null;
  /** Cancels the next poll. */
  private cancelPoll: (() => void) | undefined = undefined;
  // A newer read or change must not be overwritten by an older read.
  private requestVersion = 0;
  private latestView: JobView | null = null;

  constructor() {
    makeAutoObservable<
      StudioJobStore,
      | 'shown'
      | 'repository'
      | 'lifecycle'
      | 'cancelPoll'
      | 'requestVersion'
      | 'latestView'
    >(this, {
      error: observable.ref,
      shown: observable.ref,
      repository: false,
      lifecycle: false,
      cancelPoll: false,
      requestVersion: false,
      latestView: false,
    });
  }

  /** Names the open job inside its organization, e.g. "org1/job1". */
  get identity() {
    return scopedIdentity(this.organizationId, String(this.jobId));
  }

  /** The open job as last read or changed; null until it is first read. */
  get view(): JobView | null {
    return this.shownFor === this.identity && this.shown?.job?.id === this.jobId
      ? this.shown
      : null;
  }

  /** True while Studio drafts, generates or finishes the open job. */
  get working() {
    return !!this.view && isWorking(this.view);
  }

  /**
   * Opens a job: forgets the previous one, reads this one and keeps reading it, every 0.9 s
   * while Studio works on it and every 4 s otherwise, more slowly while it only waits (see
   * `nextPoll`), and not while the tab is hidden. Returns the function that closes it, for
   * an effect's cleanup.
   */
  open(organizationId: string | undefined, id: string | undefined) {
    this.stop();
    const lifecycle = new AbortController();
    this.lifecycle = lifecycle;
    this.organizationId = organizationId;
    this.jobId = id;
    this.latestView = null;
    this.shownFor = null;
    this.shown = null;
    this.error = null;
    let pace: Backoff | null = null;
    const tick = async () => {
      this.cancelPoll = undefined;
      const pending = this.reload(lifecycle.signal);
      const version = this.requestVersion;
      const view = await pending;
      if (lifecycle.signal.aborted) return;
      // A newer change answered while this read was out: poll as fast as that view asks.
      const current =
        view || (version !== this.requestVersion ? this.latestView : null);
      pace = nextPoll(current, !current && !!this.error, pace);
      this.cancelPoll = pollAfter(pace.delay, tick);
    };
    tick();
    return () => this.close(lifecycle);
  }

  /** Stops reading the job (by default whichever is open); late answers are ignored. */
  close(lifecycle = this.lifecycle) {
    if (!lifecycle) return;
    if (this.lifecycle === lifecycle) this.stop();
    else lifecycle.abort();
  }

  /**
   * Reads the open job now. Resolves to the view shown, or null when the read failed (see
   * `error`), was overtaken by a newer read or change, or the job was closed or replaced.
   * `identity` (from `this.identity`, captured earlier) keeps a late caller from reading a
   * job opened since.
   */
  async reload(
    signal?: AbortSignal,
    identity = this.identity,
  ): Promise<JobView | null> {
    const lifecycle = this.lifecycle;
    if (
      !lifecycle ||
      lifecycle.signal.aborted ||
      signal?.aborted ||
      this.identity !== identity
    )
      return null;
    const {organizationId, jobId: id} = this;
    const version = ++this.requestVersion;
    const controller = new AbortController();
    const abort = () => controller.abort();
    lifecycle.signal.addEventListener('abort', abort, {once: true});
    signal?.addEventListener('abort', abort, {once: true});
    const isCurrent = () =>
      !controller.signal.aborted &&
      this.lifecycle === lifecycle &&
      this.identity === identity &&
      this.requestVersion === version;
    try {
      if (!id) return null;
      const view = await this.readJob(organizationId, id, {
        signal: controller.signal,
      });
      if (!isCurrent()) return null;
      if (view?.job?.id !== id) return null;
      // An older copy of the job (e.g. from a replica) never replaces a newer one.
      const shownVersion = this.latestView?.job?.storeVersion;
      if (
        shownVersion != null &&
        view.job.storeVersion != null &&
        view.job.storeVersion < shownVersion
      )
        return this.latestView;
      this.show(view, identity);
      return view;
    } catch (e) {
      if (!isCurrent()) return null;
      runInAction(() => {
        this.error = e as Error;
      });
      return null;
    } finally {
      lifecycle.signal.removeEventListener('abort', abort);
      signal?.removeEventListener('abort', abort);
    }
  }

  /**
   * Shows the job a change returned. Returns false, showing nothing, when the job was closed
   * or replaced (or `identity` no longer matches), the view is another job's, or it is older
   * than the view shown.
   */
  setView(next: JobViewUpdate, identity = this.identity): boolean {
    if (!this.lifecycle || this.identity !== identity) return false;
    const updated = typeof next === 'function' ? next(this.latestView) : next;
    if (!updated || updated.job?.id !== this.jobId) return false;
    const previousVersion = this.latestView?.job?.storeVersion;
    const nextVersion = updated.job.storeVersion;
    if (
      previousVersion != null &&
      nextVersion != null &&
      nextVersion < previousVersion
    )
      return false;
    ++this.requestVersion;
    this.show(updated, identity);
    return true;
  }

  /**
   * Sends a change against the open job's storeVersion and shows the job it returns. Resolves
   * to that view, or null when the job was closed or replaced meanwhile (or the answer is
   * older than the view shown). Rejects with the request's error while the job is still open.
   */
  async mutate(change: (job: OpenJob) => Promise<JobView>) {
    const identity = this.identity;
    const {organizationId, jobId: id, view} = this;
    if (!this.lifecycle || !id) return null;
    try {
      const updated = await change({
        organizationId,
        id,
        storeVersion: view?.job.storeVersion,
      });
      return this.setView(updated, identity) ? updated : null;
    } catch (e) {
      if (this.lifecycle && this.identity === identity) throw e;
      return null;
    }
  }

  /**
   * Stops what Studio is doing on the open job (see `cancelJob`) and reads the job again.
   * Rejects with the request's error while the job is still open.
   */
  async cancel(storeVersion = this.view?.job.storeVersion) {
    const identity = this.identity;
    const {organizationId, jobId: id} = this;
    if (!this.lifecycle || !id) return null;
    try {
      await cancelJob(
        {
          getJob: (org, job) => this.readJob(org, job),
          cancel: (org, job, version) => this.sendCancel(org, job, version),
        },
        organizationId,
        id,
        storeVersion,
      );
    } catch (e) {
      if (this.lifecycle && this.identity === identity) throw e;
      return null;
    }
    return this.reload(undefined, identity);
  }

  private readJob(
    organizationId: string | undefined,
    id: string,
    ...options: [StudioRequestOptions?]
  ) {
    return studioRequest(organizationId, org =>
      this.repository.getJob(org, id, ...options),
    );
  }

  private sendCancel(
    organizationId: string | undefined,
    id: string,
    version: StudioVersion,
  ) {
    return studioRequest(organizationId, org =>
      this.repository.cancel(org, id, version),
    );
  }

  private show(view: JobView, identity: string) {
    this.latestView = view;
    this.shownFor = identity;
    this.shown = view;
    this.error = null;
  }

  private stop() {
    const lifecycle = this.lifecycle;
    this.lifecycle = null;
    this.cancelPoll?.();
    this.cancelPoll = undefined;
    lifecycle?.abort();
  }
}
